import {
  Availability,
  Communication,
  Discount,
  Salon,
  SalonPaymentTypes,
  Stylist,
  Tax,
  Payment,
} from '../models';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { List, Map } from 'immutable';

import { ENV_CONFIG } from '../../bin/env.config';
import { ErrorHandlerService } from './error-handler.service';
import { CalendarAvailabilities } from '../bookings/calendar/day-view-calendar/day-view-calendar.component';
import { Injectable } from '@angular/core';
import { Observable, of, firstValueFrom } from 'rxjs';
import { SalonmonsterHttpClient } from '../services/salonmonster-http-client';
import { UserService } from '../services/user.service';
import { Utilities } from '../utilities/utilities';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SalonService extends SalonmonsterHttpClient {
  public taxes: List<Tax>;

  public salon: Salon;

  constructor(
    http: HttpClient,
    userService: UserService,
    protected errorHandlerService: ErrorHandlerService
  ) {
    super(http, userService, errorHandlerService);

    userService.getLogOutMonitor().subscribe((logout) => {
      if (logout) {
        this.taxes = null;
      }
    });
  }

  public async getIsReadOnly(): Promise<boolean> {
    if (this.salon) {
      return this.salon.isReadOnly();
    }
    return false;
  }

  public getAllStaff(date: Date): Observable<List<Stylist>> {
    const url = `${
      ENV_CONFIG.API_ROOT
    }/salons/allstaff/bookings?date=${Utilities.formatDate(date)}`;
    return this.get(url).pipe(
      map((data) => {
        let stylists: List<Stylist> = List([]);

        for (const stylist of data) {
          stylists = stylists.push(Stylist.parseStylistData(stylist));
        }

        return stylists;
      })
    );
  }

  public load(): Observable<Salon> {
    const url = `${ENV_CONFIG.API_ROOT}/salons`;
    return this.get(url).pipe(
      map((data) => {
        this.salon = Salon.parse(data[0]);
        return this.salon;
      })
    );
  }

  public getTaxes(forceLoad: boolean = false): Observable<List<Tax>> {
    if (this.taxes && !forceLoad) {
      return of(this.taxes);
    } else {
      const url = `${ENV_CONFIG.API_ROOT}/salons/taxes`;
      return this.get(url).pipe(
        map((data) => {
          let taxes: List<Tax> = List([]);

          for (const tax of data) {
            taxes = taxes.push(Tax.parse(tax));
          }

          return taxes;
        }),
        tap((taxes) => {
          this.taxes = taxes;
        })
      );
    }
  }

  public saveTax(tax: Tax): Observable<Tax> {
    let url = `${ENV_CONFIG.API_ROOT}/salons/taxes`;
    if (tax.id) {
      url += '/' + tax.id;

      return this.put(url, tax.serialize()).pipe(
        map((data) => {
          return Tax.parse(data);
        })
      );
    } else {
      return this.post(url, tax.serialize()).pipe(
        map((data) => {
          return Tax.parse(data);
        })
      );
    }
  }

  public deleteTax(tax: Tax): Observable<boolean> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/taxes/${tax.id}`;

    return this.delete(url, {}).pipe(
      map((data) => {
        return data.success;
      })
    );
  }

  public updateTaxes(taxes: List<Tax>) {
    this.taxes = taxes;
  }

  public getAllStaffAvailabilities(
    year: string,
    month: string
  ): Observable<Map<string, CalendarAvailabilities>> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/allstaff/availabilities?year=${year}&month=${month}`;
    return this.get(url).pipe(
      map((data) => {
        let allStaffAvailabilities: Map<string, CalendarAvailabilities> = Map();

        for (const stylistID in data) {
          if (Object.prototype.hasOwnProperty.call(data, stylistID)) {
            const element = data[stylistID];

            allStaffAvailabilities = allStaffAvailabilities.set(
              stylistID,
              Availability.parseAvailabilityData(element)
            );
          }
        }

        return allStaffAvailabilities;
      })
    );
  }

  public saveSalon(salon: Salon): Observable<Salon> {
    const url = `${ENV_CONFIG.API_ROOT}/salons`;
    return this.put(url, salon.serialize()).pipe(
      map((data) => {
        return Salon.parse(data);
      })
    );
  }

  public getSalonPaymentTypes(): Observable<SalonPaymentTypes> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/paymenttypes`;
    return this.get(url).pipe(
      map((data) => {
        return SalonPaymentTypes.parse(data[0]);
      })
    );
  }

  public getSalonPaymentTypesObj(): Observable<any> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/paymenttypes`;
    return this.get(url).pipe(
      map((data) => {
        const paymentTypes = SalonPaymentTypes.parse(data[0]);
        let paymentTypesObj = [];
        if (paymentTypes.cash === 1) {
          paymentTypesObj.push(
            new Payment({ type: 1, name: 'Cash', amount: 0 })
          );
        }
        if (paymentTypes.debit === 1) {
          paymentTypesObj.push(
            new Payment({ type: 2, name: 'Debit', amount: 0 })
          );
        }
        if (paymentTypes.visa === 1) {
          paymentTypesObj.push(
            new Payment({ type: 3, name: 'Visa', amount: 0 })
          );
        }
        if (paymentTypes.mastercard === 1) {
          paymentTypesObj.push(
            new Payment({ type: 4, name: 'MasterCard', amount: 0 })
          );
        }
        if (paymentTypes.amex === 1) {
          paymentTypesObj.push(
            new Payment({ type: 5, name: 'AMEX', amount: 0 })
          );
        }
        if (paymentTypes.cheque === 1) {
          paymentTypesObj.push(
            new Payment({ type: 6, name: 'Check/Cheque', amount: 0 })
          );
        }
        if (paymentTypes.giftcard === 1) {
          paymentTypesObj.push(
            new Payment({ type: 7, name: 'Giftcard', amount: 0 })
          );
        }
        if (paymentTypes.etransfer === 1) {
          paymentTypesObj.push(
            new Payment({ type: 8, name: 'e-Transfer', amount: 0 })
          );
        }
        if (paymentTypes.discover === 1) {
          paymentTypesObj.push(
            new Payment({ type: 9, name: 'Discover', amount: 0 })
          );
        }
        if (paymentTypes.account === 1) {
          paymentTypesObj.push(
            new Payment({ type: 10, name: 'Account', amount: 0 })
          );
        }
        if (paymentTypes.square === 1) {
          paymentTypesObj.push(
            new Payment({ type: 11, name: 'Square', amount: 0 })
          );
        }
        if (paymentTypes.shopify === 1) {
          paymentTypesObj.push(
            new Payment({ type: 30, name: 'Shopify', amount: 0 })
          );
        }
        if (paymentTypes.giftCardExternal === 1) {
          paymentTypesObj.push(
            new Payment({ type: 31, name: 'Giftcard(external)', amount: 0 })
          );
        }
        return paymentTypesObj;
      })
    );
  }

  public savePaymentType(
    salonPaymentTypes: SalonPaymentTypes
  ): Observable<SalonPaymentTypes> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/paymenttypes`;
    return this.put(url, salonPaymentTypes.serialize()).pipe(
      map((data) => {
        return SalonPaymentTypes.parse(data);
      })
    );
  }

  public getSalonDiscounts(): Observable<List<Discount>> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/discounts`;
    return this.get(url).pipe(
      map((data) => {
        let discounts: List<Discount> = List([]);

        for (const discountData of data) {
          discounts = discounts.push(Discount.parseDiscount(discountData));
        }

        return discounts;
      })
    );
  }

  public getClientsCount(): Observable<number> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/clients/count`;
    return this.get(url).pipe(
      map((data) => {
        return data.numOfClients;
      })
    );
  }

  public saveSalonDiscount(discount: Discount): Observable<Discount> {
    let url = `${ENV_CONFIG.API_ROOT}/salons/discounts`;

    if (discount.id) {
      url += '/' + discount.id;

      return this.put(url, discount.serialize()).pipe(
        map((data) => {
          return Discount.parseDiscount(data);
        })
      );
    } else {
      return this.post(url, discount.serialize()).pipe(
        map((data) => {
          return Discount.parseDiscount(data);
        })
      );
    }
  }

  public deleteSalonDiscounts(discount: Discount): Observable<Discount> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/discounts/${discount.id}`;
    return this.delete(url, {}).pipe(
      map((data) => {
        return Discount.parseDiscount(data);
      })
    );
  }

  public bulkCommunicationHistory(): Observable<List<Communication>> {
    const url = `${
      ENV_CONFIG.API_ROOT
    }/salons/bulkcommunications?salonID=${this.userService.getSalon().getId()}`;

    return this.get(url).pipe(
      map((data) => {
        let communications: List<Communication> = List([]);

        for (const communicationData of data) {
          communications = communications.push(
            Communication.parse(communicationData)
          );
        }

        return communications;
      })
    );
  }

  public subDomainChecker(subDomain: string): Observable<boolean> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/subdomainchecker/${subDomain}`;

    return this.get(url).pipe(
      map((data) => {
        return data.available;
      })
    );
  }

  public getOnlineBookingSharePhotos(): Observable<List<string>> {
    const url = `${ENV_CONFIG.API_ROOT}/onlinebooking/share`;

    return this.get(url).pipe(
      map((data) => {
        let photos: List<string> = List([]);
        for (const photo of data) {
          photos = photos.push(photo);
        }

        return photos;
      })
    );
  }

  public deleteSalon(salonID: string, salonName: string): Observable<any> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/${salonID}/deletesalon`;
    const requestBody = {
      salonID: salonID,
      salonName: salonName,
    };

    return this.delete(url, requestBody).pipe(
      map((data) => {
        return data;
      })
    );
  }
}
